/**
 * Which half of the sidebar is showing: the agency's own pages, or one client's.
 *
 * The rules, in the order they win:
 *   1. The tab you clicked. It is a peek: it changes the sidebar, not the page,
 *      and lasts until you go somewhere else.
 *   2. The route. A client page (or the client list, a profile or a client's
 *      ads report) opens Clients;
 *      an agency page opens Agency.
 *   3. On pages that belong to neither (Settings), the tab you chose last time.
 *   4. Agency.
 *
 * Kept free of React and icon imports so node:test can load it directly
 * (see test/sidebar-mode.test.js).
 */
export type SidebarMode = "agency" | "clients";

export const SIDEBAR_MODE_KEY = "nl-aos-sidebar-mode";

export const isSidebarMode = (value: unknown): value is SidebarMode =>
  value === "agency" || value === "clients";

const matches = (pathname: string, path: string) =>
  path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(path + "/");

export function routeMode(
  pathname: string,
  clientPaths: readonly string[],
  agencyPaths: readonly string[],
): SidebarMode | undefined {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/clients" || path.startsWith("/clients/")) return "clients";
  // Before the agency list: the agency's "/ads" would otherwise claim "/ads/<id>".
  if (clientIdPage(path)) return "clients";
  if (clientPaths.some((p) => matches(path, p))) return "clients";
  if (agencyPaths.some((p) => matches(path, p))) return "agency";
  return undefined;
}

export function resolveMode(
  override: SidebarMode | undefined,
  fromRoute: SidebarMode | undefined,
  remembered: SidebarMode | undefined,
): SidebarMode {
  return override ?? fromRoute ?? remembered ?? "agency";
}

/**
 * A client page whose path names the client instead of `?client_id=`: the
 * profile (`/clients/<id>`) and the ads report (`/ads/<id>`). Returns the base
 * path and the id, or null.
 */
export function clientIdPage(pathname: string): { base: string; id: string } | null {
  const m = /^(\/clients|\/ads)\/(\d+)\/?$/.exec(pathname);
  return m ? { base: m[1], id: m[2] } : null;
}

/** A sidebar page (nav.ts NavTab without the icon): `view` is a `?view=` value. */
export type PageRef = { to: string; idInPath?: boolean; end?: boolean; view?: string };

/**
 * Is this page the one on screen? Paths match exactly or as a prefix
 * ("/knowledge/KB-007" is the Knowledge base), except "/" and `end` pages,
 * which match only themselves. A bare profile or ads report URL belongs only
 * to its `idInPath` page, so the agency's "/ads" never claims "/ads/7". A
 * `view` page needs its `?view=`, and its plain sibling on the same path then
 * steps aside.
 */
export function pageMatches(
  pathname: string,
  search: string,
  page: PageRef,
  siblings: readonly PageRef[] = [],
): boolean {
  const path = pathname.replace(/\/+$/, "") || "/";
  const idPage = clientIdPage(path);
  const onPath = page.idInPath
    ? idPage?.base === page.to
    : !idPage && (page.end || page.to === "/" ? path === page.to : matches(path, page.to));
  if (!onPath) return false;
  const view = new URLSearchParams(search).get("view");
  if (page.view) return view === page.view;
  return !siblings.some((s) => s.view && s.to === page.to && s.view === view);
}
